import { Loading } from "@/components/ui";
import { buildWaitlistHref } from "@/utils/waitlistContact";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@paribus/shared-ui";
import { Check } from "lucide-react";
import { type ComponentPropsWithoutRef, forwardRef } from "react";
import { Link, useLocation } from "react-router-dom";

type EnterpriseWaitlistContactLinkProps = Omit<ComponentPropsWithoutRef<typeof Link>, "to">;

// Waitlist branch only: keeps useLocation / href build off the mailto path.
const EnterpriseWaitlistContactLink = forwardRef<
  HTMLAnchorElement,
  EnterpriseWaitlistContactLinkProps
>(function EnterpriseWaitlistContactLink({ children = "Contact us", ...props }, ref) {
  const location = useLocation();
  return (
    <Link ref={ref} to={buildWaitlistHref(location.search)} {...props}>
      {children}
    </Link>
  );
});
EnterpriseWaitlistContactLink.displayName = "EnterpriseWaitlistContactLink";

interface EnterpriseCardProps {
  isActivated?: boolean;
  onManageSubscription?: () => void | Promise<void>;
  isLoadingManageSubscription?: boolean;
  // mailto (e.g. buildTeamMailtoHref); if omitted, Contact us links to the public waitlist.
  enterpriseContactHref?: string;
}

export const EnterpriseCard = ({
  isActivated = false,
  onManageSubscription,
  isLoadingManageSubscription = false,
  enterpriseContactHref,
}: EnterpriseCardProps) => {
  return (
    <div
      className={`relative w-full max-w-[320px] sm:max-w-72 ${isActivated ? "mt-8 md:mt-0" : ""}`}
    >
      {isActivated && (
        <div
          className="absolute -top-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-emphasis px-4 py-1 text-sm font-bold text-white shadow-md"
          role="status"
          aria-label="Enterprise plan is currently activated"
        >
          <Check className="h-4 w-4" aria-hidden="true" />
          Plan Activated
        </div>
      )}
      <Card
        className={`flex h-full w-full flex-col gap-2 rounded-2xl border-2 p-6 pb-8 ${
          isActivated ? "border-primary-emphasis bg-card shadow-xl" : "border-border"
        } transition-all hover:shadow-lg`}
      >
        <CardHeader className="flex flex-col gap-2">
          <div>
            <CardTitle className="flex items-center justify-center text-xl font-bold text-foreground">
              Enterprise
            </CardTitle>
            <p className="mt-2 min-h-[2.5rem] text-center text-sm text-muted-foreground">
              For large scale enterprises
            </p>
          </div>
          <div className="mt-6 flex flex-col items-center justify-center gap-2">
            <p className="text-3xl font-bold text-foreground">Get in touch</p>
            <div className="text-center">
              <p className="mt-1 text-sm text-muted-foreground">
                Contact sales for custom pricing & workflows
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-grow">
          {isActivated && onManageSubscription ? (
            <Button
              onClick={onManageSubscription}
              disabled={isLoadingManageSubscription}
              className="mb-6 mt-6 w-full rounded-lg border-0 bg-primary py-6 font-medium text-white transition-all hover:bg-primary-hover hover:text-white hover:shadow-lg"
            >
              {isLoadingManageSubscription ? (
                <>
                  <Loading size={16} tone="inverse" fullScreen={false} />
                  Processing...
                </>
              ) : (
                "Manage Subscription"
              )}
            </Button>
          ) : (
            <Button
              asChild
              className="mb-6 mt-6 w-full rounded-lg bg-primary py-6 font-medium text-white hover:bg-primary-hover"
            >
              {enterpriseContactHref ? (
                <a
                  href={enterpriseContactHref}
                  aria-label="Contact Paribus about Enterprise by email"
                >
                  Contact us
                </a>
              ) : (
                <EnterpriseWaitlistContactLink aria-label="Contact Paribus about Enterprise via the waitlist" />
              )}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
